import { useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { opponentColors, specialEventStyles, type GameDay } from "@/mocks/fightersSchedule";

interface GameDetailModalProps {
  game: GameDay;
  isPast: boolean;
  isToday: boolean;
  onClose: () => void;
}

function formatJpDate(dateStr: string): string {
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const days = ["日", "月", "火", "水", "木", "金", "土"];
  const dow = days[d.getDay()];
  return `${y}年${m}月${day}日（${dow}）`;
}

export default function GameDetailModal({ game, isPast, isToday, onClose }: GameDetailModalProps) {
  const [, setSearchParams] = useSearchParams();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  const handleParkingReserve = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClose();
    setSearchParams({ game_date: game.date });
    setTimeout(() => {
      document.getElementById("reserve")?.scrollIntoView({ behavior: "smooth" });
    }, 150);
  };

  const eventStyle = game.specialEvent ? specialEventStyles[game.specialEvent] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-md max-h-[calc(100dvh-2rem)] flex flex-col bg-background-50 rounded-3xl overflow-hidden animate-[scaleIn_0.25s_ease-out]">
        {/* Header with opponent color accent */}
        <div className={`h-2 shrink-0 ${opponentColors[game.opponent] || "bg-foreground-600"}`} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/90 hover:bg-background-100 text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors z-10"
          aria-label="閉じる"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          {/* Status badges */}
          <div className="flex items-center gap-2 mb-4">
            {isToday && (
              <span className="text-xs font-medium text-primary-600 bg-primary-100 rounded-full px-3 py-1">
                本日の試合
              </span>
            )}
            {isPast && (
              <span className="text-xs font-medium text-foreground-500 bg-background-200 rounded-full px-3 py-1">
                試合終了
              </span>
            )}
            {!isPast && !isToday && (
              <span className="text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full px-3 py-1">
                今後の試合
              </span>
            )}
          </div>

          {/* Date & Time */}
          <div className="mb-2">
            <p className={`text-sm ${game.isHoliday ? "text-red-500 font-medium" : "text-foreground-600"}`}>
              {game.isHoliday && (
                <span className="inline-flex items-center rounded-full bg-red-100 text-red-600 text-[10px] font-medium px-2 py-0.5 mr-1.5 align-middle">
                  祝日
                </span>
              )}
              {formatJpDate(game.date)}
            </p>
            <p className="text-3xl font-heading text-foreground-950 mt-1">
              {game.time}
              <span className="text-base text-foreground-500 ml-2">プレイボール</span>
            </p>
          </div>

          {/* Matchup */}
          {game.matchLabel ? (
            <div
              className="mt-6 py-6 px-5 bg-background-100 rounded-2xl text-center"
            >
              <span
                className={`inline-flex w-14 h-14 items-center justify-center rounded-full ${eventStyle?.bg || "bg-background-200"}`}
              >
                <i className={`${eventStyle?.icon || "ri-calendar-event-line"} text-2xl ${eventStyle?.text || "text-foreground-600"}`} />
              </span>
              <p className="mt-3 font-heading text-lg text-foreground-950">{game.matchLabel}</p>
              <p className="text-[11px] text-foreground-500 mt-1">北海道日本ハムファイターズ 主催</p>
            </div>
          ) : (
            <div className="flex items-center gap-4 mt-6 py-5 px-5 bg-background-100 rounded-2xl">
              <div className="text-center flex-1">
                <div className="text-sm font-heading text-foreground-950">北海道日本ハム</div>
                <div className="text-[11px] text-foreground-500 mt-0.5">ホーム</div>
              </div>
              <div className="flex flex-col items-center gap-1 shrink-0">
                <span className="text-[11px] font-medium text-foreground-500 bg-background-50 rounded-full px-2 py-0.5">VS</span>
                <span className={`w-4 h-4 rounded-full ${opponentColors[game.opponent] || "bg-foreground-600"}`} />
              </div>
              <div className="text-center flex-1">
                <div className="text-sm font-heading text-foreground-950">{game.opponent}</div>
                <div className="text-[11px] text-foreground-500 mt-0.5">ビジター</div>
              </div>
            </div>
          )}

          {/* Venue */}
          <div className="flex items-center gap-2 mt-4 text-sm text-foreground-600">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-foreground-500" />
            </span>
            エスコンフィールドHOKKAIDO
          </div>

          {/* Special Event Section */}
          {eventStyle && (
            <div className={`mt-5 rounded-2xl p-5 ${eventStyle.bg} bg-opacity-50`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-7 h-7 flex items-center justify-center rounded-full ${eventStyle.bg}`}>
                  <i className={`${eventStyle.icon} text-sm ${eventStyle.text}`} />
                </span>
                <span className={`font-heading text-sm ${eventStyle.text}`}>
                  特別企画：{eventStyle.label}
                </span>
              </div>
              <p className={`text-xs leading-relaxed ${eventStyle.text} opacity-80`}>
                {eventStyle.description}
              </p>
            </div>
          )}

          {/* Parking Reservation Section */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground-800">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1">
                  <i className="ri-parking-box-line text-foreground-600" />
                </span>
                駐車場予約
              </span>
              {!isPast && (
                <span className="text-xs font-medium rounded-full px-3 py-1 whitespace-nowrap bg-emerald-100 text-emerald-700">
                  予約受付中
                </span>
              )}
            </div>

            <p className="text-xs leading-relaxed text-foreground-500">
              試合日はエスコンフィールド周辺が大変混雑します。アネットパーキングなら渋滞を避けてスタジアムまで直行できます。
            </p>
          </div>
        </div>

        {/* Sticky reservation action bar (always visible) */}
        <div className="shrink-0 border-t border-background-200 bg-background-50 px-6 md:px-8 py-4">
          <a
            href="#reserve"
            onClick={isPast ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault() : handleParkingReserve}
            className={`inline-flex items-center justify-center gap-2 w-full rounded-xl py-3.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              isPast
                ? "bg-background-200 text-foreground-500 cursor-not-allowed"
                : "bg-primary-500 hover:bg-primary-600 text-background-50"
            }`}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-parking-box-line text-base" />
            </span>
            {isPast ? "この試合日の予約は終了しました" : "この試合日を駐車場予約する"}
            {!isPast && (
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-50 text-primary-600">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            )}
          </a>
        </div>
      </div>

      {/* Inline keyframe animation */}
      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}